import React, { useCallback, useEffect, useRef, useState } from "react";
import icon_camera from "./assets/camera.svg";
import icon_plus from "./assets/plus.svg";
import icon_minus from "./assets/minus.svg";
import icon_autoRotate from "./assets/autoRotate.svg";
import icon_arrows_in from "./assets/arrowsIn.svg";
import icon_back from "./assets/arrowCounterClockwise.svg";
import { TbPerspective } from "react-icons/tb";
import { getTranslations } from "./components/translations";
import { debug } from "./debug";

function Player({
  productName,
  selectedProductCode,
  selectedProduct,
  selectedMaterials = {},
  viewerMaterials = [],
  currentLanguage,
  language,
  isAdmin = false,
  onScreenshotForForm,
}) {
  const emersyaIframeRef = useRef(null);
  const perspectiveMenuRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewerActive, setViewerActive] = useState(false);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const [customViews, setCustomViews] = useState([]);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        perspectiveMenuRef.current &&
        !perspectiveMenuRef.current.contains(event.target)
      ) {
        setShowViewMenu(false);
      }
    }

    if (showViewMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showViewMenu]);

  useEffect(() => {
    if (selectedProduct) {
      try {
        loadCustomViews();
      } catch (error) {
        debug.warn.viewer("Error loading custom views:", error);
        setCustomViews([]);
      }
    } else {
      setCustomViews([]);
    }
  }, [selectedProduct, isAdmin]);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://cdn.emersya.com/f/emersyaLoader.js";
    script.id = "emersya";

    script.setAttribute("routing", selectedProductCode);
    script.setAttribute("containerId", "emersyaIframe");

    document.head.appendChild(script);

    function initializeEmersyaAPI() {
      const emersyaViewer = emViewers["emersyaIframe"];
      debug.viewer("Viewer initialized:", emersyaViewer.getCurrentProductNodesConfiguration());
      setViewerActive(true);
    }

    document.addEventListener(
      "emersyaViewerInitialized",
      initializeEmersyaAPI,
      false
    );

    return () => {
      document.removeEventListener(
        "emersyaViewerInitialized",
        initializeEmersyaAPI
      );
      document.head.removeChild(script);
      setViewerActive(false);
    };
  }, [selectedProductCode]);

  const loadCustomViews = () => {
    const views = [];

    if (
      selectedProduct?.customViews &&
      Array.isArray(selectedProduct.customViews)
    ) {
      selectedProduct.customViews.forEach((view) => {
        if (!view || typeof view !== "object" || !view.label) {
          debug.warn.viewer("Invalid product view detected:", view);
          return;
        }

        if (isAdmin || view.enabledForUsers) {
          views.push({
            ...view,
            source: "product",
            label: view.label,
            sourceType: "Prodotto",
          });
        }
      });
    }

    if (
      selectedProduct?._familyDefaults?.customViews &&
      Array.isArray(selectedProduct._familyDefaults.customViews)
    ) {
      selectedProduct._familyDefaults.customViews.forEach((view) => {
        if (!view || typeof view !== "object" || !view.label) {
          debug.warn.viewer("Invalid family view detected:", view);
          return;
        }

        if (isAdmin || view.enabledForUsers) {
          views.push({
            ...view,
            source: "family",
            label: `${view.label} (Famiglia)`,
            sourceType: "Famiglia",
          });
        }
      });
    }

    debug.viewer(`Loaded ${views.length} custom views for player:`, views);
    setCustomViews(views);
  };

  const getPresetViews = () => {
    const allViews = {
      "1A. TAVOLO PICCOLO": {
        position: [0.08793692290782928, 1.495231032371521, 4.501952648162842],
        target: [0, 0.37112775444984436, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "1A. TAVOLO PICCOLO",
        adminOnly: true,
      },
      "1B. TAVOLO GRANDE": {
        position: [
          -0.0039832619950175285, 1.2964057922363281, 5.9750800132751465,
        ],
        target: [0, 0.37262773513793945, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "1B. TAVOLO GRANDE",
        adminOnly: true,
      },
      "2A. SEDUTE": {
        position: [0.00975882913917303, 0.7702409029006958, 4.619032859802246],
        target: [0, 0.36612701416015625, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "2A. SEDUTE",
        adminOnly: true,
      },
      "2B. LIBRERIA": {
        position: [
          -0.008066613227128983, 0.5714938640594482, 3.3638663291931152,
        ],
        target: [
          -0.039580151438713074, 0.5714938640594482, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "2B. LIBRERIA",
        adminOnly: true,
      },
      FRONTALE: {
        position: [-0.032389383763074875, 0.938500165939331, 5.911214828491211],
        target: [
          -0.039580151438713074, 0.7594552040100098, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "FRONTALE",
        adminOnly: false,
        originalKey: "2C. LIBRERIA GRANDE",
      },
      "3. TAVOLI SX": {
        position: [-2.229461431503296, 1.4171435832977295, 5.5214080810546875],
        target: [0, 0.3721277713775635, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "3. TAVOLI SX",
        adminOnly: true,
      },
      "3. TAVOLI DX": {
        position: [2.229461431503296, 1.4171435832977295, 5.5214080810546875],
        target: [0, 0.3721277713775635, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "3. TAVOLI DX",
        adminOnly: true,
      },
      "4. RETRO SX": {
        position: [-2.767357587814331, 0.7379804849624634, -3.666008472442627],
        target: [0, 0.3361237943172455, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "4. RETRO SX",
        adminOnly: true,
      },
      "4. RETRO DX": {
        position: [2.767357587814331, 0.7379804849624634, -3.666008472442627],
        target: [0, 0.3361237943172455, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "4. RETRO DX",
        adminOnly: true,
      },
      "5A. SEDUTE SX": {
        position: [-3.4535088539123535, 0.8563621044158936, 4.4126434326171875],
        target: [0, 0.36612772941589355, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "5A. SEDUTE SX",
        adminOnly: true,
      },
      "5A. SEDUTE DX": {
        position: [3.4535088539123535, 0.8563621044158936, 4.4126434326171875],
        target: [0, 0.36612772941589355, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "5A. SEDUTE DX",
        adminOnly: true,
      },
      "5B. LIBRERIE SX": {
        position: [-1.86250901222229, 0.5714938640594482, 2.8582515716552734],
        target: [
          -0.039580151438713074, 0.5714938640594482, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "5B. LIBRERIE SX",
        adminOnly: true,
      },
      "5B. LIBRERIE DX": {
        position: [1.86250901222229, 0.5714938640594482, 2.8582515716552734],
        target: [
          0.039580151438713074, 0.5714938640594482, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "5B. LIBRERIE DX",
        adminOnly: true,
      },
      SINISTRA: {
        position: [-3.143932819366455, 1.0718764066696167, 4.558107376098633],
        target: [
          -0.024390466511249542, 1.0718764066696167, 0.013911008834838867,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "SINISTRA",
        adminOnly: false,
        originalKey: "5C. LIBRERIE GRANDI SX",
      },
      DESTRA: {
        position: [3.143932819366455, 1.0718764066696167, 4.558107376098633],
        target: [
          0.024390466511249542, 1.0718764066696167, 0.013911008834838867,
        ],
        up: [0, 1, 0],
        fov: 35,
        zoom: 0.4318633862569692,
        label: "DESTRA",
        adminOnly: false,
        originalKey: "5C. LIBRERIE GRANDI DX",
      },
    };

    if (isAdmin) {
      return allViews;
    } else {
      const userViews = {};
      Object.keys(allViews).forEach((key) => {
        if (!allViews[key].adminOnly) {
          userViews[key] = allViews[key];
        }
      });
      return userViews;
    }
  };

  const handleCameraViewClick = useCallback((view) => {
    if (!window.emViewers || !window.emViewers["emersyaIframe"]) {
      alert("Visualizzatore 3D non disponibile");
      return;
    }

    if (!view || typeof view !== "object") {
      debug.warn.viewer("Invalid view object:", view);
      alert("Vista non valida");
      return;
    }

    const emersyaViewer = window.emViewers["emersyaIframe"];

    try {
      if (view.source) {
        if (!view.cameraPosition || !view.cameraTarget) {
          debug.warn.viewer("Invalid custom view data:", view);
          alert("Dati vista personalizzata non validi");
          return;
        }

        const animationDuration = view.animationDuration || 1.5;
        const transitionTime = view.animate ? animationDuration * 1000 : 500;

        emersyaViewer.setCamera({
          camera: {
            position: [
              view.cameraPosition.x || 0,
              view.cameraPosition.y || 0,
              view.cameraPosition.z || 0,
            ],
            target: [
              view.cameraTarget.x || 0,
              view.cameraTarget.y || 0,
              view.cameraTarget.z || 0,
            ],
            up: [0, 1, 0],
            transitionTime: transitionTime,
            fov: view.cameraFOV || 45,
          },
        });
        debug.viewer(`Applied custom view: ${view.label}`, {
          position: view.cameraPosition,
          target: view.cameraTarget,
          fov: view.cameraFOV,
          animated: view.animate,
          duration: animationDuration,
        });
      } else {
        if (!view.position || !view.target) {
          debug.warn.viewer("Invalid preset view data:", view);
          alert("Dati vista preset non validi");
          return;
        }

        emersyaViewer.setCamera({
          camera: {
            position: view.position,
            target: view.target,
            up: view.up || [0, 1, 0],
            transitionTime: 500,
            fov: view.fov || 45,
          },
        });
        debug.viewer("Applied preset view:", view.label);
      }

      setShowViewMenu(false);
    } catch (error) {
      debug.warn.viewer("Error applying camera view:", error);
      alert("Errore nell'applicazione della vista camera");
    }
  }, []);

  const zoomIn = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    emersyaViewer.startZoomIn().then(() => {
      setTimeout(() => {
        emersyaViewer.stopZoomIn();
      }, 800);
    });
  }, []);

  const zoomOut = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    emersyaViewer.startZoomOut().then(() => {
      setTimeout(() => {
        emersyaViewer.stopZoomOut();
      }, 600);
    });
  }, []);

  const playRotation = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    emersyaViewer.play();
  }, []);

  const resetCamera = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    emersyaViewer.resetCamera({});
    emersyaViewer.play();
  }, []);

  // Helper: converte PNG dataURL in JPEG compresso
  const convertPNGtoJPEG = (pngDataUrl, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Sfondo bianco (JPEG non supporta trasparenza)
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Disegna l'immagine
        ctx.drawImage(img, 0, 0);

        // Converti in JPEG
        const jpegDataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(jpegDataUrl);
      };
      img.onerror = reject;
      img.src = pngDataUrl;
    });
  };

  const getScreenshot = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];
    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${
      date.getMonth() + 1
    }-${date.getDate()}`;

    emersyaViewer.getCamera().then((cameraData) => {
      emersyaViewer
        .getScreenshot({
          width: 1024,
          height: 1024,
          transparentBackground: false,
          camera: cameraData,
        })
        .then((imageData) => {
          const downloadLink = document.createElement("a");
          downloadLink.href = imageData;
          downloadLink.download = `${formattedDate}-${productName}.png`;
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        });
    });
  }, [productName]);
  const getScreenshotBase64ForForm = useCallback(async () => {
    if (!window.emViewers || !window.emViewers["emersyaIframe"]) {
      debug.warn.viewer("Emersya viewer non disponibile per screenshot form");
      return null;
    }

    try {
      const emersyaViewer = window.emViewers["emersyaIframe"];
      const cameraData = await emersyaViewer.getCamera();
      const imageData = await emersyaViewer.getScreenshot({
        width: 1024,
        height: 1024,
        transparentBackground: false,
        camera: cameraData,
      });

      // Converti PNG in JPEG per ridurre dimensione
      const jpegDataUrl = await convertPNGtoJPEG(imageData, 0.85); // qualità 85%
      const base64 = jpegDataUrl.split(",")[1] || "";
      return base64;
    } catch (err) {
      debug.warn.viewer("Errore screenshot per form:", err);
      return null;
    }
  }, []);

  const getCameraData = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"];

    emersyaViewer
      .getCamera()
      .then((camera) => {
        if (!camera || !camera.position || !camera.target) {
          debug.warn.viewer("Invalid camera data received:", camera);
          return;
        }

        const formattedData = {
          position: Array.from(camera.position),
          target: Array.from(camera.target),
          fov: camera.fov || 45,
          zoom: camera.zoomProportion || 1,
        };

        const output = JSON.stringify(formattedData, null, 2);

        const dialog = document.createElement("div");
        dialog.style.position = "fixed";
        dialog.style.top = "0";
        dialog.style.left = "0";
        dialog.style.width = "100%";
        dialog.style.height = "100%";
        dialog.style.backgroundColor = "rgba(0,0,0,0.5)";
        dialog.style.zIndex = "1000";
        dialog.style.display = "flex";
        dialog.style.alignItems = "center";
        dialog.style.justifyContent = "center";

        const content = document.createElement("div");
        content.style.background = "white";
        content.style.padding = "20px";
        content.style.borderRadius = "8px";
        content.style.maxWidth = "500px";
        content.style.width = "90%";

        const pre = document.createElement("pre");
        pre.style.marginBottom = "15px";
        pre.style.whiteSpace = "pre-wrap";
        pre.textContent = output;

        const copyButton = document.createElement("button");
        copyButton.textContent = "Copy Data";
        copyButton.style.padding = "8px 16px";
        copyButton.style.background = "#007bff";
        copyButton.style.color = "white";
        copyButton.style.border = "none";
        copyButton.style.borderRadius = "4px";
        copyButton.style.cursor = "pointer";
        copyButton.style.marginRight = "10px";
        copyButton.onclick = () => {
          navigator.clipboard.writeText(output).then(() => {
            copyButton.textContent = "Copied!";
            setTimeout(() => {
              copyButton.textContent = "Copy Data";
            }, 2000);
          });
        };

        const closeButton = document.createElement("button");
        closeButton.textContent = "Close";
        closeButton.style.padding = "8px 16px";
        closeButton.style.background = "#6c757d";
        closeButton.style.color = "white";
        closeButton.style.border = "none";
        closeButton.style.borderRadius = "4px";
        closeButton.style.cursor = "pointer";
        closeButton.onclick = () => dialog.remove();

        content.appendChild(pre);
        content.appendChild(copyButton);
        content.appendChild(closeButton);
        dialog.appendChild(content);
        document.body.appendChild(dialog);
      })
      .catch((error) => {
        debug.warn.viewer("Failed to get camera data:", error);
        alert("Errore nel recupero dei dati camera");
      });
  }, [viewerActive]);

  const getImageUrl = (thumbnail) => {
    if (thumbnail && thumbnail.startsWith("#")) return thumbnail;
    if (
      thumbnail &&
      (thumbnail.startsWith("http") || thumbnail.startsWith("//"))
    )
      return thumbnail;
    if (thumbnail) {
      const cleanPath = thumbnail.replace(/^\.\//, "");
      return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
    }
    return "";
  };

  const getLocalizedProductName = (product, language) => {
    if (product?.lang?.[language]) {
      return product.lang[language];
    }
    return product?.name || "N/A";
  };

  const getTranslatedTitle = (
    originalTitle,
    selectedProduct,
    translations,
    language
  ) => {
    if (
      selectedProduct?._familyDefaults?.technicalTypeTranslations?.[
        originalTitle
      ]?.[language]
    ) {
      return selectedProduct._familyDefaults.technicalTypeTranslations[
        originalTitle
      ][language];
    }

    if (translations.materialTypes?.[originalTitle.toLowerCase()]) {
      return translations.materialTypes[originalTitle.toLowerCase()];
    }

    return originalTitle;
  };

  const getVisibleSelectedMaterials = (selectedMaterials, viewerMaterials) => {
    if (!selectedMaterials || !viewerMaterials) return {};

    const visibleMaterials = {};

    const visibleMaterialTypes = viewerMaterials
      .filter((mat) => {
        return mat.variations.length >= 2;
      })
      .map((mat) => mat.technicalName);

    Object.entries(selectedMaterials).forEach(([type, material]) => {
      if (visibleMaterialTypes.includes(type)) {
        visibleMaterials[type] = material;
      }
    });

    return visibleMaterials;
  };

  // FUNZIONE INTERNA: genera l'oggetto PDF (usata sia per download che per base64)
  const createPDFDocument = useCallback(async () => {
    if (!window.emViewers || !window.emViewers["emersyaIframe"]) {
      throw new Error("Emersya viewer non disponibile");
    }

    const { jsPDF } = await import("jspdf");
    const emersyaViewer = window.emViewers["emersyaIframe"];
    const translations = getTranslations(language || "it");

    const cameraData = await emersyaViewer.getCamera();
    const pngImageData = await emersyaViewer.getScreenshot({
      width: 1024,
      height: 576,
      transparentBackground: false,
      camera: cameraData,
    });

    const imageData = await convertPNGtoJPEG(pngImageData, 0.88);

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = 210;
    const pageHeight = 297;
    const leftMargin = 15;
    const rightMargin = 15;
    const contentWidth = pageWidth - leftMargin - rightMargin;

    let yPosition = 12;

    // Header
    pdf.setFontSize(8);
    pdf.setFont(undefined, "normal");
    pdf.text("Steel Design", leftMargin, yPosition);
    pdf.text("dieffebi.com", leftMargin + 30, yPosition);

    pdf.setFontSize(10);
    pdf.setFont(undefined, "bold");
    const dieffebiBoldText = "DIEFFEBI";
    const dieffebiBoldWidth = pdf.getTextWidth(dieffebiBoldText);
    pdf.text(
      dieffebiBoldText,
      pageWidth - rightMargin - dieffebiBoldWidth,
      yPosition
    );

    yPosition += 8;

    // Date/time
    const now = new Date();
    const dateStr = now.toLocaleDateString(
      language === "en"
        ? "en-US"
        : language === "fr"
        ? "fr-FR"
        : language === "de"
        ? "de-DE"
        : "it-IT",
      { day: "2-digit", month: "2-digit", year: "numeric" }
    );
    const timeStr = now.toLocaleTimeString(
      language === "en"
        ? "en-US"
        : language === "fr"
        ? "fr-FR"
        : language === "de"
        ? "de-DE"
        : "it-IT",
      { hour: "2-digit", minute: "2-digit" }
    );

    pdf.setFontSize(8);
    pdf.setFont(undefined, "normal");
    pdf.text(
      `${translations.pdf.generatedOn}: ${dateStr} alle ${timeStr}`,
      leftMargin,
      yPosition
    );

    pdf.setFontSize(9);
    pdf.setFont(undefined, "bold");
    const rightText = translations.pdf.madeInItaly;
    const rightTextWidth = pdf.getTextWidth(rightText);
    pdf.text(rightText, pageWidth - rightMargin - rightTextWidth, yPosition);

    yPosition += 5;

    // Separator line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(leftMargin, yPosition, pageWidth - rightMargin, yPosition);

    yPosition += 6;

    // Product info
    pdf.setFontSize(9);
    pdf.setFont(undefined, "normal");

    const familyName =
      selectedProduct?.family || selectedProduct?._family || "N/A";
    pdf.text(`${translations.pdf.gamma}: ${familyName}`, leftMargin, yPosition);

    yPosition += 5;

    const productCode =
      selectedProduct?.onpage_code || selectedProduct?.id || "N/A";
    pdf.text(`${translations.pdf.code}: ${productCode}`, leftMargin, yPosition);

    yPosition += 8;

    // Screenshot
    const imgWidth = contentWidth;
    const imgHeight = (imgWidth * 9) / 16;

    pdf.addImage(imageData, "JPEG", leftMargin, yPosition, imgWidth, imgHeight);

    yPosition += imgHeight + 6;

    // Product name
    const localizedProductName = getLocalizedProductName(
      selectedProduct,
      language || "it"
    );

    pdf.setFontSize(10);
    pdf.setFont(undefined, "bold");
    const productNameLines = pdf.splitTextToSize(
      localizedProductName,
      contentWidth
    );
    productNameLines.forEach((line) => {
      pdf.text(line, leftMargin, yPosition);
      yPosition += 5;
    });

    yPosition += 3;

    pdf.setLineWidth(0.3);
    pdf.line(leftMargin, yPosition, pageWidth - rightMargin, yPosition);

    yPosition += 6;

    // Materials
    const visibleSelectedMaterials = getVisibleSelectedMaterials(
      selectedMaterials,
      viewerMaterials
    );

    if (
      visibleSelectedMaterials &&
      Object.keys(visibleSelectedMaterials).length > 0
    ) {
      pdf.setFontSize(9);
      pdf.setFont(undefined, "bold");
      pdf.text(translations.pdf.selectedMaterials + ":", leftMargin, yPosition);
      yPosition += 6;

      pdf.setFont(undefined, "normal");

      for (const [type, material] of Object.entries(visibleSelectedMaterials)) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = 20;
        }

        const thumbnailSize = 5;
        const thumbnailY = yPosition - 3;

        if (material.thumbnail) {
          try {
            const imageUrl = getImageUrl(material.thumbnail);

            if (imageUrl.startsWith("#")) {
              const hex = imageUrl.replace("#", "");
              const r = parseInt(hex.substr(0, 2), 16);
              const g = parseInt(hex.substr(2, 2), 16);
              const b = parseInt(hex.substr(4, 2), 16);
              pdf.setFillColor(r, g, b);
              pdf.rect(
                leftMargin,
                thumbnailY,
                thumbnailSize,
                thumbnailSize,
                "F"
              );
            } else if (imageUrl) {
              await new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                  try {
                    pdf.addImage(
                      img,
                      "PNG",
                      leftMargin,
                      thumbnailY,
                      thumbnailSize,
                      thumbnailSize
                    );
                    resolve();
                  } catch (e) {
                    debug.warn.viewer("Failed to add image:", e);
                    pdf.setFillColor(200, 200, 200);
                    pdf.rect(
                      leftMargin,
                      thumbnailY,
                      thumbnailSize,
                      thumbnailSize,
                      "F"
                    );
                    resolve();
                  }
                };
                img.onerror = () => {
                  pdf.setFillColor(200, 200, 200);
                  pdf.rect(
                    leftMargin,
                    thumbnailY,
                    thumbnailSize,
                    thumbnailSize,
                    "F"
                  );
                  resolve();
                };
                img.src = imageUrl;
              });
            }
          } catch (error) {
            debug.warn.viewer("Error loading thumbnail:", error);
            pdf.setFillColor(200, 200, 200);
            pdf.rect(leftMargin, thumbnailY, thumbnailSize, thumbnailSize, "F");
          }
        } else {
          pdf.setFillColor(200, 200, 200);
          pdf.rect(leftMargin, thumbnailY, thumbnailSize, thumbnailSize, "F");
        }

        const translatedType = getTranslatedTitle(
          type,
          selectedProduct,
          translations,
          language || "it"
        );
        const materialName =
          material.displayName || material.name || material.viewerName || "N/A";

        pdf.text(
          `${translatedType}: ${materialName}`,
          leftMargin + thumbnailSize + 2,
          yPosition
        );
        yPosition += 6;
      }

      yPosition += 2;
    }

    // Price
    if (selectedProduct?.price && typeof selectedProduct.price === "number") {
      pdf.setFontSize(10);
      pdf.setFont(undefined, "bold");
      pdf.text(
        `${
          translations.pdf.startingFrom
        }: €${selectedProduct.price.toLocaleString("it-IT")}`,
        leftMargin,
        yPosition
      );
      yPosition += 8;
    }

    // Footer
    const footerY = pageHeight - 25;
    pdf.setLineWidth(0.3);
    pdf.line(leftMargin, footerY, pageWidth - rightMargin, footerY);

    let footerTextY = footerY + 5;
    pdf.setFontSize(7);
    pdf.setFont(undefined, "normal");

    const addressLines = [
      translations.pdf.footerCompany,
      translations.pdf.footerAddress,
      translations.pdf.footerCity,
      translations.pdf.footerPostal,
    ];

    addressLines.forEach((line, index) => {
      pdf.text(line, leftMargin, footerTextY + index * 3);
    });

    const contactsX = leftMargin + 50;
    const contactLines = [
      translations.pdf.footerPhone,
      translations.pdf.footerExportSales,
      translations.pdf.footerSales,
      translations.pdf.footerMarketing,
      translations.pdf.footerWebsite,
    ];

    contactLines.forEach((line, index) => {
      pdf.text(line, contactsX, footerTextY + index * 3);
    });

    return { pdf, now }; // Ritorna l'oggetto PDF e la data per il filename
  }, [selectedProduct, selectedMaterials, viewerMaterials, language]);

  // FUNZIONE PUBBLICA: genera e scarica il PDF (per l'utente)
  const generatePDF = useCallback(
    async (includeForm = false, contactData = null) => {
      if (isGeneratingPDF) return;

      setIsGeneratingPDF(true);

      try {
        const { pdf, now } = await createPDFDocument();

        // Se ci sono dati del form, aggiungili
        if (includeForm && contactData) {
          const translations = getTranslations(language || "it");
          let yPosition = pdf.internal.pageSize.height - 80; // Posizione per info cliente

          pdf.setFontSize(9);
          pdf.setFont(undefined, "bold");
          pdf.text(translations.pdf.clientInfo + ":", 15, yPosition);
          yPosition += 6;

          pdf.setFont(undefined, "normal");

          const clientInfo = [];
          if (contactData.nome || contactData.cognome) {
            clientInfo.push(
              `${translations.pdf.name}: ${contactData.nome || ""} ${
                contactData.cognome || ""
              }`.trim()
            );
          }
          if (contactData.email)
            clientInfo.push(`${translations.pdf.email}: ${contactData.email}`);
          if (contactData.telefono)
            clientInfo.push(
              `${translations.pdf.phone}: ${contactData.telefono}`
            );
          if (contactData.azienda)
            clientInfo.push(
              `${translations.pdf.company}: ${contactData.azienda}`
            );

          clientInfo.forEach((info) => {
            pdf.text(info, 15, yPosition);
            yPosition += 5;
          });
        }

        // Genera filename
        const formattedDate = `${now.getFullYear()}-${String(
          now.getMonth() + 1
        ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        const formattedTime = `${String(now.getHours()).padStart(
          2,
          "0"
        )}_${String(now.getMinutes()).padStart(2, "0")}`;
        const fileProductCode =
          selectedProduct?.onpage_code ||
          selectedProduct?.code ||
          selectedProduct?.id ||
          "PRODOTTO";

        // SCARICA il file
        pdf.save(
          `${formattedDate}-${formattedTime}-${fileProductCode}-configurazione.pdf`
        );

        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        debug.warn.viewer("Errore nella generazione PDF:", error);
        alert("Errore nella generazione del PDF. Riprova.");
      } finally {
        setIsGeneratingPDF(false);
      }
    },
    [createPDFDocument, selectedProduct, language, isGeneratingPDF]
  );

  // FUNZIONE PER IL FORM: genera PDF in base64 (senza download)
  const generatePDFBase64ForForm = useCallback(async () => {
    try {
      const { pdf } = await createPDFDocument();
      const pdfDataUri = pdf.output("datauristring");
      const base64 = pdfDataUri.split(",")[1] || "";
      return base64;
    } catch (error) {
      debug.warn.viewer("Errore nella generazione PDF per form:", error);
      return null;
    }
  }, [createPDFDocument]);

  useEffect(() => {
    window.generatePDF = generatePDF;
    window.getConfiguratorScreenshotBase64 = getScreenshotBase64ForForm;
    window.getConfiguratorPDFBase64 = generatePDFBase64ForForm; // AGGIUNTO

    return () => {
      delete window.generatePDF;
      delete window.getConfiguratorScreenshotBase64;
      delete window.getConfiguratorPDFBase64; // AGGIUNTO
    };
  }, [generatePDF, getScreenshotBase64ForForm, generatePDFBase64ForForm]);

  const getViewCategories = () => {
    const presetViews = getPresetViews();
    const categories = {};

    if (isAdmin) {
      categories["VISTE FRONTALI"] = [
        "1A. TAVOLO PICCOLO",
        "1B. TAVOLO GRANDE",
        "2A. SEDUTE",
        "2B. LIBRERIA",
        "FRONTALE",
      ].filter((key) => presetViews[key]);

      categories["VISTE LATERALI"] = [
        "3. TAVOLI SX",
        "3. TAVOLI DX",
        "4. RETRO SX",
        "4. RETRO DX",
        "5A. SEDUTE SX",
        "5A. SEDUTE DX",
        "5B. LIBRERIE SX",
        "5B. LIBRERIE DX",
        "SINISTRA",
        "DESTRA",
      ].filter((key) => presetViews[key]);
    } else {
      categories["VISTE STANDARD"] = ["FRONTALE", "SINISTRA", "DESTRA"].filter(
        (key) => presetViews[key]
      );
    }

    if (Array.isArray(customViews) && customViews.length > 0) {
      const productCustomViews = customViews.filter(
        (view) => view && view.source === "product" && view.label
      );
      const familyCustomViews = customViews.filter(
        (view) => view && view.source === "family" && view.label
      );

      if (productCustomViews.length > 0) {
        categories["VISTE PRODOTTO"] = productCustomViews;
      }

      if (familyCustomViews.length > 0) {
        categories["VISTE FAMIGLIA"] = familyCustomViews;
      }
    }

    return { categories, presetViews };
  };

  return (
    <div className="ay-col-span-4 md:ay-col-span-3 md:ay-sticky ay-top-0 ay-h-fit ay-relative">
      <div className="ay-z-10 ay-absolute ay-left-5 ay-bottom-5 ay-flex ay-flex-col ay-gap-1">
        {isAdmin && (
          <p
            onClick={getCameraData}
            className="hover:ay-cursor-pointer ay-mt-2 ay-text-center ay-leading-[10px] ay-text-[8px]"
          >
            CAMERA <br /> DATA
          </p>
        )}

        <img
          onClick={playRotation}
          className="hover:ay-cursor-pointer ay-w-8"
          src={icon_autoRotate}
          alt="Auto Rotate"
        />
        <img
          onClick={zoomIn}
          className="hover:ay-cursor-pointer ay-w-8"
          src={icon_plus}
          alt="Zoom In"
        />
        <img
          onClick={zoomOut}
          className="hover:ay-cursor-pointer ay-w-8"
          src={icon_minus}
          alt="Zoom Out"
        />
        <img
          onClick={resetCamera}
          className="hover:ay-cursor-pointer ay-w-8"
          src={icon_back}
          alt="Reset Camera"
        />
        <img
          onClick={getScreenshot}
          className="hover:ay-cursor-pointer ay-w-8"
          src={icon_camera}
          alt="Take Screenshot"
        />
        <div
          onClick={() => !isGeneratingPDF && generatePDF()}
          className={`hover:ay-cursor-pointer ay-flex ay-flex-col ay-justify-center ay-align-middle ay-items-center ay-mb-1 ay-border-2 ay-rounded-full ay-border-black ay-w-7 ay-self-center ay-h-7 ${
            isGeneratingPDF ? "ay-opacity-50 ay-cursor-not-allowed" : ""
          }`}
        >
          {isGeneratingPDF ? (
            <svg
              className="ay-animate-spin ay-h-4 ay-w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="ay-opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="ay-opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <p className="ay-text-center ay-leading-[10px] ay-text-[8px]">
              PDF
            </p>
          )}
        </div>

        <div ref={perspectiveMenuRef} className="">
          <div
            onClick={() => setShowViewMenu(!showViewMenu)}
            className="ay-w-8 ay-h-8 ay-flex ay-items-center ay-justify-center ay-bg-white ay-rounded-full ay-shadow-md"
          >
            <TbPerspective className="ay-w-6 ay-h-6 hover:ay-cursor-pointer" />
          </div>

          {showViewMenu && (
            <div className="ay-absolute ay-ml-2 ay-mb-5 ay-left-full ay-bottom-0 ay-bg-white ay-shadow-lg ay-py-2 ay-z-50 ay-max-h-96 ay-overflow-y-auto">
              <div className="ay-flex ay-flex-col">
                {(() => {
                  const { categories, presetViews } = getViewCategories();

                  return Object.entries(categories).map(
                    ([category, viewKeys]) => (
                      <React.Fragment key={category}>
                        <div className="ay-text-[9px] ay-font-bold ay-px-4 ay-py-1 ay-bg-gray-100">
                          {category}
                        </div>
                        {Array.isArray(viewKeys) && viewKeys.length > 0
                          ? typeof viewKeys[0] === "string"
                            ? viewKeys.map((viewKey) => (
                                <button
                                  key={viewKey}
                                  onClick={() =>
                                    handleCameraViewClick(presetViews[viewKey])
                                  }
                                  className="ay-text-left ay-text-sm ay-px-4 ay-py-2 hover:ay-bg-gray-100 ay-text-[8px] ay-min-w-[140px] ay-whitespace-nowrap"
                                >
                                  {presetViews[viewKey].label}
                                </button>
                              ))
                            : viewKeys
                                .filter((view) => view && view.label)
                                .map((view, index) => (
                                  <button
                                    key={`${view.source}-${index}-${view.label}`}
                                    onClick={() => handleCameraViewClick(view)}
                                    className="ay-text-left ay-text-sm ay-px-4 ay-py-2 hover:ay-bg-gray-100 ay-text-[8px] ay-min-w-[140px] ay-whitespace-nowrap ay-flex ay-items-center ay-justify-between"
                                    title={`Vista ${view.sourceType}: ${
                                      view.label
                                    }${view.animate ? " (animata)" : ""}`}
                                  >
                                    <span>{view.label}</span>
                                    <div className="ay-flex ay-items-center ay-gap-1 ay-ml-2">
                                      {view.source === "family" && (
                                        <span className="ay-text-[6px] ay-bg-green-100 ay-text-green-600 ay-px-1 ay-rounded">
                                          F
                                        </span>
                                      )}
                                      {view.source === "product" && (
                                        <span className="ay-text-[6px] ay-bg-blue-100 ay-text-blue-600 ay-px-1 ay-rounded">
                                          P
                                        </span>
                                      )}
                                      {view.animate && (
                                        <span className="ay-text-[6px] ay-bg-yellow-100 ay-text-yellow-600 ay-px-1 ay-rounded">
                                          🎬
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                ))
                          : null}
                      </React.Fragment>
                    )
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className="ay-h-[45vh] lg:ay-h-[calc(100vh-100px)] ay-w-full ay-relative"
        id="emersyaIframe"
        ref={emersyaIframeRef}
      ></div>
    </div>
  );
}

export default Player;
